// Conservative, template legal content for BeetDrop's public site.
// IMPORTANT: placeholders in [brackets] and all wording are subject to the
// business's own legal review before relying on them. No unverified technical
// or security guarantees are asserted here.

export const LEGAL_LAST_UPDATED = "June 2026";

export const LEGAL_DISCLAIMER =
  "This document is a general template provided for transparency. It is not legal advice and is subject to review by BeetDrop's legal advisers. Items shown in [brackets] must be completed with the operator's registered details before publication.";

export const LEGAL_DOCS = [
  {
    slug: "terms-of-service",
    title: "Terms of Service",
    intro: "These Terms of Service govern access to and use of the BeetDrop platform by hospitality businesses and their authorised users.",
    sections: [
      { heading: "1. Agreement", body: [
        "By registering for or using BeetDrop, you agree to these Terms of Service on behalf of your business. If you do not agree, you should not use the platform.",
      ] },
      { heading: "2. Accounts and access", body: [
        "Business accounts are created during onboarding and require a verified email address and acceptance of these terms.",
        "Each staff account is tied to a specific business, location, department and role. You are responsible for the activity of accounts created under your business and for keeping credentials secure.",
      ] },
      { heading: "3. Acceptable use", body: [
        "You agree to use BeetDrop only for lawful hospitality operations and not to misuse the platform, interfere with its operation, or attempt to access data belonging to another business.",
      ] },
      { heading: "4. Subscriptions and trials", body: [
        "BeetDrop is provided on a subscription basis. New businesses may receive a 30-day free trial with full platform access, no setup fee and no long-term contract. Pricing and plan details are set out on our Pricing page and in your account.",
      ] },
      { heading: "5. Availability", body: [
        "We aim to keep BeetDrop available but do not guarantee uninterrupted access. The platform is provided on an \"as is\" and \"as available\" basis to the extent permitted by law.",
      ] },
      { heading: "6. Changes and termination", body: [
        "We may update these terms from time to time; material changes will be communicated through the platform. You may stop using BeetDrop at any time, and we may suspend access where these terms are breached.",
      ] },
      { heading: "7. Contact", body: [
        "Questions about these terms can be sent to [support email] or [registered business address].",
      ] },
    ],
  },
  {
    slug: "terms-and-conditions",
    title: "Terms & Conditions",
    intro: "These Terms & Conditions apply to guests and other end users who access participating services through BeetDrop.",
    sections: [
      { heading: "1. Guest accounts", body: [
        "Guests may create an account to submit reservation requests and raise service issues with participating businesses. You agree to provide accurate information.",
      ] },
      { heading: "2. Reservation requests", body: [
        "Reservation requests submitted through BeetDrop are requests only and are subject to approval by the relevant business. A request does not constitute a confirmed booking until approved.",
      ] },
      { heading: "3. Information you provide", body: [
        "Information you provide — such as party size, dietary requirements, allergies and notes — is shared with the relevant business so your request can be handled. You are responsible for the accuracy of this information.",
      ] },
      { heading: "4. Conduct", body: [
        "You agree not to submit unlawful, abusive or deliberately false content through the platform.",
      ] },
      { heading: "5. Relationship with businesses", body: [
        "Services are provided by the participating businesses, not by BeetDrop. Any contract for a service is between you and that business.",
      ] },
    ],
  },
  {
    slug: "privacy-gdpr",
    title: "Privacy Policy & GDPR",
    intro: "This policy explains, in general terms, how personal data is handled when using BeetDrop and the rights available under the UK GDPR and EU GDPR where applicable.",
    sections: [
      { heading: "1. Who is responsible", body: [
        "For guest and operational data entered into a business's workspace, that business is the data controller and BeetDrop acts as a processor on its behalf. For account and billing data of the business itself, [Company Legal Name] is the controller.",
      ] },
      { heading: "2. Data we handle", body: [
        "Depending on use, this may include account details (name, email, role), business configuration (locations, departments, services, staff), reservation requests, dietary and allergy information, and service issue records.",
      ] },
      { heading: "3. Purpose and lawful basis", body: [
        "Personal data is processed to provide the platform's operational features — routing information to the correct people, managing reservations and resolving service issues — on the basis of contract performance and the legitimate interests of operating the service.",
      ] },
      { heading: "4. Data separation", body: [
        "Each business's data is kept logically separated so that one business cannot access another business's locations, staff, guests, reservations or operational records.",
      ] },
      { heading: "5. Your rights", body: [
        "Subject to applicable law, individuals may request access to their personal data, correction, erasure, restriction or portability, and may object to certain processing. Requests relating to data held within a business's workspace should be directed to that business.",
      ] },
      { heading: "6. Retention", body: [
        "Personal data is retained for as long as needed to provide the service and to meet legal obligations, after which it is deleted or anonymised. Specific retention periods will be confirmed by the operator.",
      ] },
      { heading: "7. Contact and complaints", body: [
        "Privacy queries can be sent to [data protection contact]. You also have the right to lodge a complaint with your local supervisory authority (for example, the ICO in the UK).",
      ] },
    ],
  },
  {
    slug: "cookie-policy",
    title: "Cookie Policy",
    intro: "This policy describes how cookies and similar technologies are used on the BeetDrop website and platform.",
    sections: [
      { heading: "1. What we use", body: [
        "BeetDrop uses cookies and similar storage that are strictly necessary to provide core functionality, such as keeping you signed in securely during a session.",
      ] },
      { heading: "2. Non-essential cookies", body: [
        "Where any analytics or non-essential cookies are introduced, they will only be used in line with applicable law and, where required, with your consent.",
      ] },
      { heading: "3. Managing cookies", body: [
        "You can control cookies through your browser settings. Disabling strictly necessary cookies may prevent parts of the platform from working.",
      ] },
    ],
  },
  {
    slug: "refund-policy",
    title: "Refund & Cancellation Policy",
    intro: "This policy explains cancellations and refunds for BeetDrop business subscriptions.",
    sections: [
      { heading: "1. Free trial", body: [
        "New businesses may use the platform free for 30 days with no setup fee and no obligation. If you do not continue after the trial, no subscription charge applies.",
      ] },
      { heading: "2. Subscriptions", body: [
        "Paid subscriptions are billed in advance on a monthly or annual basis according to the plan selected. Annual plans are offered with two months free compared to the monthly rate.",
      ] },
      { heading: "3. Cancellation", body: [
        "You may cancel your subscription at any time. Cancellation stops future renewals; access continues until the end of the current paid period. There is no long-term contract.",
      ] },
      { heading: "4. Refunds", body: [
        "Unless required by law, payments already made for a current billing period are generally non-refundable. Where a refund is due by law or agreed in writing, it will be processed to the original payment method.",
      ] },
      { heading: "5. Founding Customer Programme", body: [
        "Founding customers retain their introductory rate while their qualifying subscription remains active.",
      ] },
      { heading: "6. Contact", body: [
        "Billing questions can be sent to [billing email].",
      ] },
    ],
  },
  {
    slug: "acceptable-use",
    title: "Acceptable Use Policy",
    intro: "This policy sets out acceptable use of the BeetDrop platform.",
    sections: [
      { heading: "1. Permitted use", body: [
        "BeetDrop may be used for legitimate hospitality operations and guest communication only.",
      ] },
      { heading: "2. Prohibited activity", body: [
        "You must not attempt to access other businesses' data, disrupt the service, upload unlawful or harmful content, or use the platform to send unsolicited messages.",
      ] },
      { heading: "3. Enforcement", body: [
        "We may suspend or remove access where this policy is breached, and may be required to cooperate with lawful requests from authorities.",
      ] },
    ],
  },
];

export const legalDoc = (slug) => LEGAL_DOCS.find((d) => d.slug === slug);
